import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SELECTED_AUDIO_OUTPUT_KEY } from "config/livekit";
import useDevices from "hooks/livekit/useDevices/useDevices";

export default function useActiveAudioSinkId() {
  const { audioOutputDevices } = useDevices();
  const [activeAudioSinkId, _setActiveAudioSinkId] = useState("default");

  const setActiveAudioSinkId = useCallback(
    (sinkId) => {
      window.localStorage.setItem(SELECTED_AUDIO_OUTPUT_KEY, sinkId);
      _setActiveAudioSinkId(sinkId);
    },
    [_setActiveAudioSinkId]
  );

  // Restore previously selected device from localStorage
  useEffect(() => {
    const selectedSinkId = window.localStorage.getItem(
      SELECTED_AUDIO_OUTPUT_KEY
    );
    const hasSelectedAudioOutputDevice = audioOutputDevices.some(
      (device) => selectedSinkId && device.deviceId === selectedSinkId
    );
    if (hasSelectedAudioOutputDevice) {
      _setActiveAudioSinkId(selectedSinkId);
    }
  }, [audioOutputDevices]);

  // A pinned speaker that gets unplugged would otherwise stay selected
  // forever: the provider's sink check finds no label for it and skips every
  // apply, so re-picking the same entry is a no-op. Follow the OS default
  // instead, which is where LiveKit has already moved the session.
  useEffect(() => {
    if (audioOutputDevices.length === 0) return;
    if (activeAudioSinkId === "default") return;
    if (audioOutputDevices.some((device) => device.deviceId === activeAudioSinkId)) return;
    setActiveAudioSinkId("default");
  }, [audioOutputDevices, activeAudioSinkId, setActiveAudioSinkId]);

  // Auto-select newly connected audio output device
  const prevOutputIdsRef = useRef(null);
  const outputIds = useMemo(
    () => audioOutputDevices.map((d) => d.deviceId),
    [audioOutputDevices]
  );
  useEffect(() => {
    const prev = prevOutputIdsRef.current;
    prevOutputIdsRef.current = outputIds;
    if (!prev || prev.length === 0 || outputIds.length === 0) return;
    const newId = outputIds.find((id) => !prev.includes(id));
    if (newId) {
      setActiveAudioSinkId(newId);
    }
  }, [outputIds, setActiveAudioSinkId]);

  return [activeAudioSinkId, setActiveAudioSinkId];
}
